import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../AuthContext';
import API, {
  getAderenti, getRicorrentiNellAtto, aggiungiAderente, modificaAderente, eliminaAderente, spostaAderente, scaricaAderenti,
  getCampiPersonalizzati, salvaCampiPersonalizzati, getAtti, creaAtto, modificaAtto, eliminaAtto, aggiungiAderenteAtto, aggiungiTuttiAtto, modificaAderenteAtto, togliAderenteAtto,
  uploadDocumenti,
} from '../api';
import CaricaDocumenti from './CaricaDocumenti';
import { NuovoClienteForm } from './Clienti';
import { msgErrore, lordoDaNetto, senzaRotella } from '../utils/formati';
import { depositato } from '../utils/statiRicorso';

// Ricorsi collettivi (docs/SPEC_ricorsi_collettivi.md §4): card "Ricorrenti" e "Atti successivi"
// della scheda collettiva. Le regole (stati, compenso, rinunzia) stanno nel backend; qui solo la
// presentazione, i controlli di comodità e le conferme.

export const ACCENTO_RICORRENTI = '#C05621';   // distinto da azzurro (documenti), verde (collegati), viola (sentenza)
const ACCENTO_ATTI = '#4A5568';
const PER_PAGINA = 50;

export const STATI_ADESIONE = {
  in_attesa_documentazione: { etichetta: 'In attesa di documentazione', colore: '#B7791F', sfondo: '#FFFAF0' },
  documentazione_completa: { etichetta: 'Documentazione completa', colore: '#276749', sfondo: '#F0FFF4' },
  rinunzia: { etichetta: 'Rinunzia', colore: '#4A5568', sfondo: '#EDF2F7' },
  ritirato: { etichetta: 'Ritirato', colore: '#C53030', sfondo: '#FFF5F5' },
};

// Stessa regola di collettivi.compenso_adesione nel backend: tenerle allineate.
// GRATIS = 0 (confermato dallo studio); altra convenzione = prezzo convenzionati; nessuna = pieno.
export const CONVENZIONE_GRATIS = 'GRATIS';
export function compensoAdesione(convenzione, compenso, compensoConvenzionati) {
  const conv = (convenzione || '').trim().toUpperCase();
  if (conv === CONVENZIONE_GRATIS) return 0;
  const v = conv ? compensoConvenzionati : compenso;
  return v === null || v === undefined || v === '' ? null : Number(v);
}

export const fmtEur = (v) => (v === null || v === undefined || v === '' ? '—'
  : Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }));
export const fmtData = (iso) => (iso ? `${iso.substring(8, 10)}/${iso.substring(5, 7)}/${iso.substring(0, 4)}` : '—');
export const nominativo = (a) => `${(a.cognome || '').toUpperCase()} ${a.nome || ''}`.trim();

// Conferma "Sei sicuro?" ogni volta che un ricorrente viene tolto da un ricorso o da un atto (§4)
export function confermaUscita(azione, aderente, codice) {
  return window.confirm(`Sei sicuro?\n\n${azione}: ${nominativo(aderente)}${aderente.codice_fiscale ? ` (${aderente.codice_fiscale})` : ''}\n${codice}`);
}

// Invio in corso: il pulsante si disattiva e un secondo clic non riparte. Il ref blocca anche due
// clic ravvicinati, arrivati prima che React ridisegni il pulsante disattivato.
function useInvio() {
  const attivo = useRef(false);
  const [inCorso, setInCorso] = useState(false);
  const invia = useCallback(async (fn) => {
    if (attivo.current) return undefined;
    attivo.current = true;
    setInCorso(true);
    try { return await fn(); } finally { attivo.current = false; setInCorso(false); }
  }, []);
  return [inCorso, invia];
}

function testoBlob(b) {
  return new Promise((ok, ko) => {
    const r = new FileReader();
    r.onload = () => ok(r.result);
    r.onerror = () => ko(r.error);
    r.readAsText(b);
  });
}

async function scarica(promessa, nome) {
  try {
    const r = await promessa;
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = nome;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    return null;
  } catch (e) {
    let detail = null;
    try { detail = JSON.parse(await testoBlob(e.response.data)).detail; } catch (_) { /* risposta non JSON */ }
    return msgErrore(detail ? { response: { data: { detail } } } : e, 'File non generato');
  }
}

// ── Stili ─────────────────────────────────────────────────────────────────────

const card = (colore) => ({ marginTop: 18, padding: '12px 16px 14px', borderRadius: 8,
  background: 'white', border: `1px solid ${colore}40`, borderLeft: `4px solid ${colore}` });
const testata = (colore) => ({ fontSize: 12, fontWeight: 600, color: colore, paddingBottom: 6, marginBottom: 10,
  borderBottom: `2px solid ${colore}25`, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  flexWrap: 'wrap', gap: 8 });
const btn = (colore, pieno = false, disabilitato = false) => ({
  padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 500, cursor: disabilitato ? 'not-allowed' : 'pointer',
  border: `1px solid ${colore}`, background: pieno ? colore : 'white', color: pieno ? 'white' : colore,
  opacity: disabilitato ? 0.5 : 1,
});
const th = { textAlign: 'left', padding: '5px 8px', fontSize: 10, color: '#718096', fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1px solid #e8eaed', whiteSpace: 'nowrap' };
const td = { padding: '5px 8px', fontSize: 12, borderBottom: '1px solid #f1f3f5', verticalAlign: 'top' };
const etichetta = { fontSize: 10, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 2 };
const nota = { background: '#FFFBF0' };
const errore = { color: '#C53030', fontSize: 12, margin: '6px 0' };
const riquadro = { background: '#FAFAFA', border: '1px solid #edf2f7', borderRadius: 6, padding: 10, margin: '6px 0' };

export function BadgeStatoAdesione({ stato, motivo }) {
  const s = STATI_ADESIONE[stato] || { etichetta: stato, colore: '#718096', sfondo: '#EDF2F7' };
  return (
    <span title={motivo || undefined} style={{ padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
      color: s.colore, background: s.sfondo, border: `1px solid ${s.colore}40`, whiteSpace: 'nowrap' }}>
      {s.etichetta}
    </span>
  );
}

// ── Stampa ed export: scelta delle colonne (regole del 22/10) ──────────────────
// Fisse Cognome, Nome e Codice fiscale; le altre da spuntare, nessuna di default. Stesse colonne nel
// PDF e nell'Excel; il backend le ordina, le controlla (400) e le registra in audit.
const COLONNE_FISSE = ['Cognome', 'Nome', 'Codice fiscale'];
const COLONNE_FACOLTATIVE = [['codice_cliente', 'Codice cliente'], ['convenzione', 'Convenzione'], ['capofila', 'Capofila'],
  ['stato', 'Stato'], ['data_adesione', 'Data adesione'], ['data_stato', 'Data stato'], ['motivo', 'Motivo'],
  ['compenso', 'Compenso'], ['compenso_pagato', 'Pagato'], ['note', 'Note'], ['n_documenti', 'Documenti']];

function SceltaColonne({ ricorso, formato, campi, conInAttesa, onErrore, onChiudi }) {
  const [stato, setStato] = useState(formato === 'stampa' ? 'documentazione_completa' : 'tutti');
  const [scelte, setScelte] = useState(new Set());
  const [inviando, invia] = useInvio();
  // "Note documentazione mancante" solo se la scheda ha ricorrenti in attesa di documentazione
  const voci = [['email', 'Email'], ...campi.map(c => [`campo:${c.id}`, c.etichetta]), ...COLONNE_FACOLTATIVE,
    ...(conInAttesa ? [['note_documentazione', 'Note documentazione mancante']] : [])];
  const cambia = (k) => setScelte(s => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const pdf = formato === 'stampa';
  const genera = () => invia(async () => {
    const colonne = voci.map(([k]) => k).filter(k => scelte.has(k));
    onErrore(await scarica(scaricaAderenti(ricorso.id, formato, stato, colonne),
      `aderenti_${ricorso.codice_ricorso}.${pdf ? 'pdf' : 'xlsx'}`));
  });
  const casella = { display: 'flex', gap: 4, alignItems: 'center', fontSize: 12 };
  return (
    <div style={riquadro} role="group" aria-label={pdf ? 'Colonne della stampa' : "Colonne dell'export"}>
      <div style={{ fontSize: 11, fontWeight: 700, color: ACCENTO_RICORRENTI, textTransform: 'uppercase', marginBottom: 6 }}>
        {pdf ? 'Stampa elenco (PDF)' : 'Esporta (Excel)'}: colonne</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
        {COLONNE_FISSE.map(t => <label key={t} style={casella}><input type="checkbox" checked disabled /> {t}</label>)}
        {voci.map(([k, t]) => (
          <label key={k} style={casella}><input type="checkbox" checked={scelte.has(k)} onChange={() => cambia(k)} /> {t}</label>))}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <label style={casella}>Ricorrenti
          <select className="input-filtro" style={{ fontSize: 11, padding: '2px 4px' }} value={stato}
            onChange={e => setStato(e.target.value)} title="Ricorrenti da includere">
            {Object.entries(STATI_ADESIONE).map(([k, s]) => <option key={k} value={k}>{s.etichetta}</option>)}
            <option value="tutti">Tutti</option>
          </select></label>
        <span style={{ fontSize: 11, color: '#718096' }}>in ordine alfabetico{pdf ? '; con molte colonne il PDF è in orizzontale' : ''}</span>
        <button style={btn(ACCENTO_RICORRENTI, true, inviando)} disabled={inviando} onClick={genera}>
          {pdf ? 'Genera PDF' : 'Genera Excel'}</button>
        <button style={btn('#718096')} onClick={onChiudi}>Chiudi</button>
      </div>
    </div>
  );
}

// ── Card Ricorrenti ───────────────────────────────────────────────────────────

// ── Campi personalizzati (regole del 22/10) ───────────────────────────────────
// Definizioni sulla scheda, modificabili fino al deposito; valori facoltativi per ogni ricorrente,
// correggibili anche dopo. Le regole stanno nel backend (collettivi.normalizza_campi).

// Elenco di campi da aggiungere, rinominare e togliere: [{ id?, etichetta }]. id assente = campo nuovo.
export function EditorCampi({ campi, onChange }) {
  const cambia = (i, etichetta) => onChange(campi.map((c, j) => (j === i ? { ...c, etichetta } : c)));
  return (
    <div>
      {campi.map((c, i) => (
        <div key={c.id || `nuovo-${i}`} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
          <input className="input-filtro" style={{ minWidth: 240 }} value={c.etichetta} aria-label={`Etichetta del campo ${i + 1}`}
            placeholder="es. Classe di concorso" onChange={e => cambia(i, e.target.value)} />
          <button type="button" style={btn('#C53030')} onClick={() => onChange(campi.filter((_, j) => j !== i))}>Togli</button>
        </div>
      ))}
      <button type="button" style={btn(ACCENTO_RICORRENTI)} onClick={() => onChange([...campi, { etichetta: '' }])}>+ Aggiungi campo</button>
    </div>
  );
}

function CampiDelRicorso({ ricorso, campi, onSalvati }) {
  const [bozza, setBozza] = useState(campi.campi.map(c => ({ id: c.id, etichetta: c.etichetta })));
  const [err, setErr] = useState(null);
  const [inviando, invia] = useInvio();
  const salva = () => invia(async () => {
    setErr(null);
    try { await salvaCampiPersonalizzati(ricorso.id, bozza); onSalvati(); }
    catch (e) { setErr(msgErrore(e, 'Campi non salvati')); }
  });
  return (
    <div style={riquadro}>
      <div style={{ fontSize: 11, color: '#718096', marginBottom: 6 }}>
        Campi di testo per ogni ricorrente (es. Classe di concorso, Provincia): si cambiano fino al deposito.
        Un campo tolto non si mostra più, ma i valori già inseriti restano salvati.</div>
      <EditorCampi campi={bozza} onChange={setBozza} />
      {err && <div style={errore}>{err}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button style={btn(ACCENTO_RICORRENTI, true, inviando)} disabled={inviando} onClick={salva}>Salva campi</button>
      </div>
    </div>
  );
}

// valori dei campi personalizzati nei form del ricorrente
function ValoriCampi({ campi, valori, onChange }) {
  if (!campi.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
      {campi.map(c => (
        <label key={c.id}><span style={etichetta}>{c.etichetta}</span>
          <input className="input-filtro" style={{ width: 160 }} value={valori[c.id] || ''}
            onChange={e => onChange({ ...valori, [c.id]: e.target.value })} /></label>
      ))}
    </div>
  );
}

const CampoNoteDocumentazione = ({ value, onChange }) => (
  <label style={{ display: 'block', marginTop: 8 }}><span style={etichetta}>Documentazione mancante</span>
    <textarea className="input-filtro" rows={2} style={{ width: '100%', ...nota }} value={value}
      placeholder="es. procura firmata, documento d'identità" onChange={e => onChange(e.target.value)} /></label>
);

// solo i valori cambiati, per i campi attuali ('' = valore tolto)
function valoriCambiati(campi, iniziali, attuali) {
  const out = {};
  campi.forEach(c => { if ((attuali[c.id] || '') !== (iniziali[c.id] || '')) out[c.id] = attuali[c.id] || ''; });
  return out;
}

const MSG_STATO_DOPO_DEPOSITO = 'Ricorso depositato: lo stato passa solo da In attesa di documentazione a '
  + 'Documentazione completa; la rinunzia passa da un atto di rinunzia';

function FormAderente({ aderente, onSalva, onAnnulla, statoBloccato, campi = [] }) {
  const [f, setF] = useState({
    stato: aderente.stato, data_adesione: aderente.data_adesione || '', data_stato: aderente.data_stato || '',
    motivo: aderente.motivo || '', compenso: aderente.compenso ?? '', compenso_pagato: !!aderente.compenso_pagato,
    note: aderente.note || '', note_documentazione: aderente.note_documentazione || '',
  });
  const [valori, setValori] = useState(aderente.valori_personalizzati || {});
  const inAttesa = aderente.stato === 'in_attesa_documentazione';     // note sulla documentazione solo per loro
  const [err, setErr] = useState(null);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const inRinunzia = aderente.stato === 'rinunzia';
  // ricorso depositato: a mano solo da in attesa di documentazione a documentazione completa (regole del 22/10)
  const ammessi = statoBloccato
    ? (aderente.stato === 'in_attesa_documentazione' ? ['in_attesa_documentazione', 'documentazione_completa'] : [aderente.stato])
    : null;
  const fisso = inRinunzia || (statoBloccato && ammessi.length === 1);
  const salva = async () => {
    if (f.stato === 'ritirato' && !f.motivo.trim()) { setErr('Per Ritirato serve il motivo'); return; }
    const iniziale = { stato: aderente.stato, data_adesione: aderente.data_adesione || '', data_stato: aderente.data_stato || '',
      motivo: aderente.motivo || '', compenso: aderente.compenso ?? '', compenso_pagato: !!aderente.compenso_pagato,
      note: aderente.note || '', note_documentazione: aderente.note_documentazione || '' };
    const v = {};
    Object.keys(f).forEach(k => { if (String(f[k]) !== String(iniziale[k])) v[k] = f[k]; });
    if (fisso) delete v.stato;
    if (!inAttesa) delete v.note_documentazione;
    ['data_adesione', 'data_stato', 'motivo', 'note', 'note_documentazione'].forEach(k => { if (k in v && v[k] === '') v[k] = null; });
    const cambiati = valoriCambiati(campi, aderente.valori_personalizzati || {}, valori);
    if (Object.keys(cambiati).length) v.valori_personalizzati = cambiati;
    if ('compenso' in v) v.compenso = v.compenso === '' ? null : Number(v.compenso);
    if (!Object.keys(v).length) { onAnnulla(); return; }
    try { await onSalva(v); } catch (e) { setErr(msgErrore(e, 'Salvataggio non riuscito')); }
  };
  return (
    <div style={riquadro}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        <label><span style={etichetta}>Stato</span>
          <select className="input-filtro" value={f.stato} disabled={fisso} onChange={e => set('stato', e.target.value)}
            title={inRinunzia ? "Deriva dall'atto di rinunzia: si cambia dagli Atti successivi"
              : statoBloccato ? MSG_STATO_DOPO_DEPOSITO : undefined}>
            {Object.entries(STATI_ADESIONE).map(([k, s]) => (
              <option key={k} value={k} disabled={(k === 'rinunzia' && !inRinunzia) || (ammessi && !ammessi.includes(k))}>
                {s.etichetta}</option>))}
          </select></label>
        <label><span style={etichetta}>Data adesione</span>
          <input type="date" className="input-filtro" value={f.data_adesione} onChange={e => set('data_adesione', e.target.value)} /></label>
        <label><span style={etichetta}>Data stato</span>
          <input type="date" className="input-filtro" value={f.data_stato} onChange={e => set('data_stato', e.target.value)} /></label>
        <label><span style={etichetta}>Compenso (€)</span>
          <input type="number" step="0.01" min="0" onWheel={senzaRotella} className="input-filtro" style={{ width: 100 }} value={f.compenso}
            onChange={e => set('compenso', e.target.value)} /></label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
          <input type="checkbox" checked={f.compenso_pagato} onChange={e => set('compenso_pagato', e.target.checked)} /> Pagato</label>
      </div>
      {f.stato === 'ritirato' && (
        <label style={{ display: 'block', marginTop: 8 }}><span style={etichetta}>Motivo *</span>
          <input className="input-filtro" style={{ width: '100%', ...nota }} value={f.motivo}
            placeholder="ritiro, requisiti mancanti, fuori termine…" onChange={e => set('motivo', e.target.value)} /></label>
      )}
      <ValoriCampi campi={campi} valori={valori} onChange={setValori} />
      {inAttesa && <CampoNoteDocumentazione value={f.note_documentazione} onChange={v => set('note_documentazione', v)} />}
      <label style={{ display: 'block', marginTop: 8 }}><span style={etichetta}>Note</span>
        <textarea className="input-filtro" rows={2} style={{ width: '100%', ...nota }} value={f.note}
          onChange={e => set('note', e.target.value)} /></label>
      {err && <div style={errore}>{err}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button style={btn(ACCENTO_RICORRENTI, true)} onClick={salva}>Salva</button>
        <button style={btn('#718096')} onClick={onAnnulla}>Annulla</button>
      </div>
    </div>
  );
}

// Nuovo cliente dal riquadro "Aggiungi ricorrente": lo stesso form della pagina Clienti, con lo stesso
// endpoint (POST /clienti, permesso clienti.crea, cartella creata dal backend). Salvato, il cliente
// torna al riquadro già scelto. L'errore del backend resta nella modale, con i dati inseriti.
// Usata anche nel riquadro del cliente del form ricorso (SchedaRicorso.IntestatarioBox).
export function ModaleNuovoCliente({ onCreato, onChiudi }) {
  const [err, setErr] = useState(null);
  return (
    <div role="dialog" aria-label="Nuovo cliente" style={{ position: 'fixed', inset: 0, background: 'rgba(26,26,46,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
      <div style={{ background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 900 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#276749', marginBottom: 16 }}>➕ Nuovo cliente</div>
        {err && <div style={errore}>{err}</div>}
        <NuovoClienteForm onCancel={onChiudi} onSave={async (dati) => {
          setErr(null);
          try {
            const r = await API.post('/clienti', dati);
            onCreato({ ...dati, codice_cliente: r.data.codice_cliente });
          } catch (e) { setErr(msgErrore(e, 'Cliente non creato')); throw e; }
        }} />
      </div>
    </div>
  );
}

// Registrazione completa del ricorrente (27/10): cliente, stato iniziale, compenso, pagato, data,
// note, campi personalizzati e documenti. L'adesione è una sola chiamata; i file si caricano dopo
// nel fascicolo personale e un loro errore non annulla l'adesione: resta scritto nel pannello.
const STATI_INIZIALI = ['in_attesa_documentazione', 'documentazione_completa'];
// data di adesione proposta: oggi (giorno locale), modificabile dall'operatore. È la data
// che il report Campagne usa per l'adesione del ricorrente
const oggiISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function testoScartati(scartati) {
  return (scartati || []).map(s => `${s.file} (${s.motivo})`).join('; ');
}

function AggiungiRicorrente({ ricorso, giaAderenti, onAggiunto, onChiudi, campi = [] }) {
  const { haPermesso } = useAuth();
  const [nuovoCliente, setNuovoCliente] = useState(false);
  const [q, setQ] = useState('');
  const [risultati, setRisultati] = useState([]);
  const [scelto, setScelto] = useState(null);
  const [stato, setStato] = useState(STATI_INIZIALI[0]);
  const [dataAdesione, setDataAdesione] = useState(oggiISO);
  const [compenso, setCompenso] = useState('');
  const [pagato, setPagato] = useState(false);
  const [note, setNote] = useState('');
  const [valori, setValori] = useState({});
  const [noteDoc, setNoteDoc] = useState('');
  const [files, setFiles] = useState([]);
  const [sottocartella, setSottocartella] = useState('');
  const [err, setErr] = useState(null);
  const [aggiunto, setAggiunto] = useState(null);       // nominativo dell'ultimo ricorrente salvato
  const ricercaRef = useRef(null);
  const cerca = async () => {
    if (q.trim().length < 2) return;
    try {
      const r = await API.get(`/clienti?q=${encodeURIComponent(q.trim())}&per_pagina=8`);
      setRisultati(Array.isArray(r.data) ? r.data : (r.data?.clienti || r.data?.risultati || []));
    } catch (e) { setErr(msgErrore(e, 'Ricerca non riuscita')); }
  };
  const svuota = () => {
    setQ(''); setRisultati([]); setScelto(null); setStato(STATI_INIZIALI[0]); setDataAdesione(oggiISO()); setCompenso('');
    setPagato(false); setNote(''); setValori({}); setNoteDoc(''); setFiles([]); setSottocartella('');
  };
  useEffect(() => { if (!scelto && aggiunto) ricercaRef.current?.focus(); }, [scelto, aggiunto]);
  const previsto = scelto ? compensoAdesione(scelto.convenzione, ricorso.compenso, ricorso.compenso_convenzionati) : null;
  const inAttesa = stato === 'in_attesa_documentazione';
  const [inviando, invia] = useInvio();
  const salva = (altro) => invia(async () => {
    setErr(null); setAggiunto(null);
    const nome = nominativo(scelto);
    try {
      const conValori = valoriCambiati(campi, {}, valori);
      await aggiungiAderente(ricorso.id, { codice_cliente: scelto.codice_cliente, data_adesione: dataAdesione || null,
        compenso: compenso === '' ? null : Number(compenso),
        ...(stato !== STATI_INIZIALI[0] ? { stato } : {}),
        ...(pagato ? { compenso_pagato: true } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(Object.keys(conValori).length ? { valori_personalizzati: conValori } : {}),
        ...(inAttesa && noteDoc.trim() ? { note_documentazione: noteDoc.trim() } : {}) });
    } catch (e) { setErr(msgErrore(e, 'Ricorrente non aggiunto')); return; }
    let erroreDocumenti = null;
    if (files.length) {
      try {
        const r = await uploadDocumenti('ricorso', ricorso.id, files, files.map(() => sottocartella.trim()), undefined,
          scelto.codice_cliente);
        if (r.data?.n_scartati) erroreDocumenti = testoScartati(r.data.scartati);
      } catch (e) { erroreDocumenti = msgErrore(e, 'caricamento non riuscito'); }
    }
    if (erroreDocumenti) {
      // l'adesione c'è: il pannello resta aperto con l'errore, i documenti si caricano da "Documenti"
      svuota();
      setErr(`Ricorrente aggiunto (${nome}), ma i documenti non sono stati caricati: ${erroreDocumenti}. `
        + 'Caricali dal pulsante Documenti del ricorrente.');
      onAggiunto(true);
    } else if (altro) {
      svuota();
      setAggiunto(nome);
      onAggiunto(true);
    } else {
      onAggiunto(false);
    }
  });
  return (
    <div style={riquadro}>
      {aggiunto && <div style={{ fontSize: 12, color: '#276749', marginBottom: 6 }}>Aggiunto: {aggiunto}</div>}
      {!scelto ? (
        <>
          <div style={{ display: 'flex', gap: 6 }}>
            <input ref={ricercaRef} className="input-filtro" style={{ flex: 1 }} placeholder="Cerca cliente per cognome, nome o codice fiscale"
              value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && cerca()} autoFocus />
            <button style={btn(ACCENTO_RICORRENTI, true)} onClick={cerca}>Cerca</button>
            {haPermesso('clienti.crea') && <button style={btn('#276749')} onClick={() => setNuovoCliente(true)}>Nuovo cliente</button>}
            <button style={btn('#718096')} onClick={onChiudi}>Chiudi</button>
          </div>
          {nuovoCliente && <ModaleNuovoCliente onChiudi={() => setNuovoCliente(false)}
            onCreato={(c) => { setNuovoCliente(false); setScelto(c); }} />}
          {risultati.map(c => {
            const gia = giaAderenti.has(c.codice_cliente);
            return (
              <div key={c.codice_cliente} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '5px 4px', borderBottom: '1px solid #edf2f7', fontSize: 12 }}>
                <span>{nominativo(c) || c.ragione_sociale} <span style={{ color: '#718096' }}>{c.codice_fiscale}
                  {c.convenzione ? ` · ${c.convenzione}` : ''}</span></span>
                {gia ? <span style={{ color: '#718096' }}>già aderente</span>
                  : <button style={btn(ACCENTO_RICORRENTI)} onClick={() => { setAggiunto(null); setScelto(c); }}>Scegli</button>}
              </div>
            );
          })}
        </>
      ) : (
        <>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            <b>{nominativo(scelto)}</b> <span style={{ color: '#718096' }}>{scelto.codice_fiscale}
              {` · convenzione: ${scelto.convenzione || 'nessuna'}`}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            <label><span style={etichetta}>Stato iniziale</span>
              <select className="input-filtro" value={stato} onChange={e => setStato(e.target.value)} aria-label="Stato iniziale">
                {STATI_INIZIALI.map(s => <option key={s} value={s}>{STATI_ADESIONE[s].etichetta}</option>)}
              </select></label>
            <div><span style={etichetta}>Compenso che verrà applicato</span>
              <b data-testid="compenso-previsto">{fmtEur(previsto)}</b>
              {previsto === null && <span style={{ fontSize: 11, color: '#B7791F' }}> (prezzo non indicato sulla scheda)</span>}</div>
            <label><span style={etichetta}>Compenso diverso (€)</span>
              <input type="number" step="0.01" min="0" onWheel={senzaRotella} className="input-filtro" style={{ width: 100 }} value={compenso}
                onChange={e => setCompenso(e.target.value)} /></label>
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, paddingBottom: 6 }}>
              <input type="checkbox" checked={pagato} onChange={e => setPagato(e.target.checked)} /> Pagato</label>
            <label><span style={etichetta}>Data adesione</span>
              <input type="date" className="input-filtro" value={dataAdesione} onChange={e => setDataAdesione(e.target.value)} /></label>
          </div>
          <label style={{ display: 'block', marginTop: 8 }}><span style={etichetta}>Note</span>
            <textarea className="input-filtro" rows={2} style={{ width: '100%', ...nota }} value={note}
              onChange={e => setNote(e.target.value)} /></label>
          {inAttesa && <CampoNoteDocumentazione value={noteDoc} onChange={setNoteDoc} />}
          <ValoriCampi campi={campi} valori={valori} onChange={setValori} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginTop: 8 }}>
            <label><span style={etichetta}>Documenti da caricare</span>
              <input type="file" multiple aria-label="Documenti da caricare" disabled={inviando}
                onChange={e => { setFiles(Array.from(e.target.files || [])); }} /></label>
            {files.length > 0 && (
              <label><span style={etichetta}>Sottocartella (facoltativa)</span>
                <input className="input-filtro" style={{ width: 180 }} value={sottocartella} placeholder="es. PROCURA"
                  onChange={e => setSottocartella(e.target.value)} /></label>)}
          </div>
          {files.length > 0 && (
            <div style={{ fontSize: 11, color: '#4A5568', marginTop: 4 }}>
              {files.map((f, i) => <div key={`${i}-${f.name}`}>{f.name}</div>)}
              <span style={{ color: '#718096' }}>Si caricano nel fascicolo personale dopo il salvataggio del ricorrente.</span>
            </div>)}
          {err && <div style={errore}>{err}</div>}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button style={btn(ACCENTO_RICORRENTI, true, inviando)} disabled={inviando} onClick={() => salva(false)}>Salva</button>
            <button style={btn(ACCENTO_RICORRENTI, false, inviando)} disabled={inviando} onClick={() => salva(true)}>Salva e aggiungi un altro</button>
            <button style={btn('#718096')} disabled={inviando} onClick={() => setScelto(null)}>Indietro</button>
          </div>
        </>
      )}
      {!scelto && err && <div style={errore}>{err}</div>}
    </div>
  );
}

export function CardRicorrenti({ ricorso, versione = 0, onCambio }) {
  const { haPermesso } = useAuth();
  const puoModificare = haPermesso('ricorsi.modifica');
  const puoDocumenti = haPermesso('documenti.leggi');
  const [aderenti, setAderenti] = useState([]);
  const [filtro, setFiltro] = useState('tutti');
  const [ricerca, setRicerca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [err, setErr] = useState(null);
  const [aperto, setAperto] = useState(null);        // {codice, cosa: 'modifica'|'documenti'|'sposta'}
  const [destinazione, setDestinazione] = useState('');
  const [aggiungi, setAggiungi] = useState(false);
  const [file, setFile] = useState(null);           // 'stampa' | 'export': scelta delle colonne aperta
  const [campi, setCampi] = useState({ campi: [], modificabili: false });
  const [editorCampi, setEditorCampi] = useState(false);
  const chiuso = depositato(ricorso);     // ricorso depositato: elenco dei ricorrenti chiuso

  const carica = useCallback(() => {
    getAderenti(ricorso.id).then(r => setAderenti(r.data)).catch(e => setErr(msgErrore(e, 'Elenco ricorrenti non disponibile')));
    getCampiPersonalizzati(ricorso.id).then(r => setCampi(r.data || { campi: [], modificabili: false }))
      .catch(() => setCampi({ campi: [], modificabili: false }));
  }, [ricorso.id]);
  useEffect(() => { carica(); }, [carica, versione]);

  const cambiato = () => { carica(); onCambio && onCambio(); };
  const conteggi = useMemo(() => aderenti.reduce((acc, a) => ({ ...acc, [a.stato]: (acc[a.stato] || 0) + 1 }), {}), [aderenti]);
  const visibili = useMemo(() => {
    const t = ricerca.trim().toLowerCase();
    return aderenti.filter(a => (filtro === 'tutti' || a.stato === filtro)
      && (!t || `${a.cognome} ${a.nome} ${a.nome} ${a.cognome} ${a.codice_fiscale || ''}`.toLowerCase().includes(t)));
  }, [aderenti, filtro, ricerca]);
  const pagine = Math.max(1, Math.ceil(visibili.length / PER_PAGINA));
  const righe = visibili.slice((pagina - 1) * PER_PAGINA, pagina * PER_PAGINA);
  useEffect(() => { setPagina(1); }, [filtro, ricerca]);

  const esegui = async (promessa, fallback) => {
    setErr(null);
    try { await promessa(); setAperto(null); cambiato(); } catch (e) { setErr(msgErrore(e, fallback)); }
  };
  const elimina = (a) => {
    if (!confermaUscita('Eliminare l\'adesione', a, ricorso.codice_ricorso)) return;
    esegui(() => eliminaAderente(ricorso.id, a.codice_cliente), 'Adesione non eliminata');
  };
  const [spostando, inviaSpostamento] = useInvio();
  const sposta = (a) => inviaSpostamento(async () => {
    const dest = destinazione.trim().toUpperCase();
    if (!dest) return;
    if (!confermaUscita(`Spostare su ${dest}`, a, `da ${ricorso.codice_ricorso} a ${dest}`)) return;
    await esegui(async () => {
      const r = await spostaAderente(ricorso.id, a.codice_cliente, dest);
      if (r.data?.avviso) window.alert(r.data.avviso);
      setDestinazione('');
    }, 'Spostamento non riuscito');
  });
  const apri = (codice, cosa) => setAperto(x => (x && x.codice === codice && x.cosa === cosa ? null : { codice, cosa }));

  return (
    <div style={card(ACCENTO_RICORRENTI)}>
      <div style={testata(ACCENTO_RICORRENTI)}>
        <span>Ricorrenti <span style={{ color: '#718096', fontWeight: 500 }}>· {aderenti.length}</span></span>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {puoModificare && !chiuso && <button style={btn(ACCENTO_RICORRENTI, true)} onClick={() => setAggiungi(v => !v)}>+ Aggiungi ricorrente</button>}
          {puoModificare && campi.modificabili && <button style={btn(ACCENTO_RICORRENTI)} onClick={() => setEditorCampi(v => !v)}>
            Campi personalizzati</button>}
          {chiuso && <span style={{ fontSize: 11, color: '#718096', fontWeight: 500 }}>
            Ricorso depositato: i nuovi ricorrenti vanno in una nuova tornata</span>}
          <button style={btn(ACCENTO_RICORRENTI, file === 'stampa')} onClick={() => setFile(f => (f === 'stampa' ? null : 'stampa'))}>
            Stampa elenco</button>
          <button style={btn(ACCENTO_RICORRENTI, file === 'export')} onClick={() => setFile(f => (f === 'export' ? null : 'export'))}>
            Esporta</button>
        </span>
      </div>
      {file && <SceltaColonne key={file} ricorso={ricorso} formato={file} campi={campi.campi}
        conInAttesa={aderenti.some(a => a.stato === 'in_attesa_documentazione')} onErrore={setErr} onChiudi={() => setFile(null)} />}
      {editorCampi && campi.modificabili && <CampiDelRicorso ricorso={ricorso} campi={campi}
        onSalvati={() => { setEditorCampi(false); carica(); }} />}
      {aggiungi && !chiuso && <AggiungiRicorrente ricorso={ricorso} campi={campi.campi} giaAderenti={new Set(aderenti.map(a => a.codice_cliente))}
        onChiudi={() => setAggiungi(false)} onAggiunto={(resta) => { if (!resta) setAggiungi(false); cambiato(); }} />}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        {[['tutti', 'Tutti', aderenti.length], ...Object.entries(STATI_ADESIONE).map(([k, s]) => [k, s.etichetta, conteggi[k] || 0])]
          .map(([k, testo, n]) => (
            <button key={k} onClick={() => setFiltro(k)} style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, cursor: 'pointer',
              border: `1px solid ${filtro === k ? ACCENTO_RICORRENTI : '#e2e8f0'}`,
              background: filtro === k ? ACCENTO_RICORRENTI : 'white', color: filtro === k ? 'white' : '#4A5568' }}>
              {testo} · {n}</button>))}
        <input className="input-filtro" style={{ marginLeft: 'auto', minWidth: 220, fontSize: 12 }}
          placeholder="Cerca per nome o codice fiscale" value={ricerca} onChange={e => setRicerca(e.target.value)} />
      </div>
      {err && <div style={errore}>{err}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>Ricorrente</th><th style={th}>Codice fiscale</th>
            {campi.campi.map(c => <th key={c.id} style={th}>{c.etichetta}</th>)}
            <th style={th}>Convenzione</th>
            <th style={th}>Adesione</th><th style={th}>Stato</th><th style={{ ...th, textAlign: 'right' }}>Compenso</th>
            <th style={th}>Pagato</th><th style={th}>Doc.</th><th style={th} />
          </tr></thead>
          <tbody>
            {righe.map(a => {
              const qui = aperto && aperto.codice === a.codice_cliente ? aperto.cosa : null;
              // prima del deposito, capofila compreso (poi si ricalcola): si spostano i ricorrenti attivi,
              // si eliminano quelli in attesa di documentazione senza documenti
              const spostabile = !chiuso && ['in_attesa_documentazione', 'documentazione_completa'].includes(a.stato);   // attivi
              const eliminabile = !chiuso && a.stato === 'in_attesa_documentazione' && !a.n_documenti;
              return (
                <React.Fragment key={a.codice_cliente}>
                  <tr>
                    <td style={td}>{nominativo(a)}
                      {a.capofila && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: ACCENTO_RICORRENTI }}>CAPOFILA</span>}
                      {a.stato === 'in_attesa_documentazione' && a.note_documentazione && (
                        <div style={{ ...nota, fontSize: 11, padding: '1px 6px', borderRadius: 4, marginTop: 2 }}>
                          Manca: {a.note_documentazione}</div>)}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{a.codice_fiscale || '—'}</td>
                    {campi.campi.map(c => <td key={c.id} style={td}>{(a.valori_personalizzati || {})[c.id] || '—'}</td>)}
                    <td style={td}>{a.convenzione || '—'}</td>
                    <td style={td}>{fmtData(a.data_adesione)}</td>
                    <td style={td}><BadgeStatoAdesione stato={a.stato} motivo={a.motivo} /></td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtEur(a.compenso)}</td>
                    <td style={td}>{a.compenso_pagato ? 'Sì' : 'No'}</td>
                    <td style={td}>{a.n_documenti || 0}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {puoModificare && <button style={btn(ACCENTO_RICORRENTI)} onClick={() => apri(a.codice_cliente, 'modifica')}>Modifica</button>}{' '}
                      {puoDocumenti && <button style={btn('#2B6CB0')} onClick={() => apri(a.codice_cliente, 'documenti')}>Documenti</button>}{' '}
                      {puoModificare && spostabile && <button style={btn('#4A5568')} onClick={() => apri(a.codice_cliente, 'sposta')}>Sposta</button>}{' '}
                      {puoModificare && eliminabile &&
                        <button style={btn('#C53030')} onClick={() => elimina(a)}>Elimina</button>}
                    </td>
                  </tr>
                  {qui && (
                    <tr><td colSpan={9 + campi.campi.length} style={{ padding: '0 8px' }}>
                      {qui === 'modifica' && <FormAderente aderente={a} statoBloccato={chiuso} campi={campi.campi} onAnnulla={() => setAperto(null)}
                        onSalva={async (v) => { await modificaAderente(ricorso.id, a.codice_cliente, v); setAperto(null); cambiato(); }} />}
                      {qui === 'documenti' && <CaricaDocumenti tipo="ricorso" id={ricorso.id} aderente={a.codice_cliente}
                        titolo={`Documenti personali di ${nominativo(a)}`} />}
                      {qui === 'sposta' && (
                        <div style={{ ...riquadro, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12 }}>Sposta su un'altra tornata non depositata della stessa iniziativa
                            ({ricorso.codice_ricorso.substring(0, 15)}…):</span>
                          <input className="input-filtro" style={{ minWidth: 260 }} placeholder="Codice della tornata di destinazione"
                            value={destinazione} onChange={e => setDestinazione(e.target.value)} />
                          <button style={btn('#4A5568', true, spostando)} disabled={spostando} onClick={() => sposta(a)}>Conferma spostamento</button>
                        </div>
                      )}
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
            {!righe.length && <tr><td colSpan={9 + campi.campi.length} style={{ ...td, color: '#718096' }}>Nessun ricorrente.</td></tr>}
          </tbody>
        </table>
      </div>
      {pagine > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 8, fontSize: 12 }}>
          <button style={btn('#718096', false, pagina === 1)} disabled={pagina === 1} onClick={() => setPagina(p => p - 1)}>‹</button>
          <span>Pagina {pagina} di {pagine} · {visibili.length} ricorrenti</span>
          <button style={btn('#718096', false, pagina === pagine)} disabled={pagina === pagine} onClick={() => setPagina(p => p + 1)}>›</button>
        </div>
      )}
    </div>
  );
}

// ── Card Atti successivi ──────────────────────────────────────────────────────

const TIPI_ATTO = { motivi_aggiunti: 'Motivi aggiunti', rinunzia: 'Rinunzia', esecuzione_provvedimento: 'Esecuzione provvedimento' };
// sottocartella del fascicolo personale per i documenti di un atto (27/10): "<TIPO> - <descrizione>",
// solo il tipo senza descrizione; le barre diventano trattini (sul server separano le cartelle)
const CARTELLE_ATTO = { motivi_aggiunti: 'MOTIVI AGGIUNTI', rinunzia: 'RINUNZIA', esecuzione_provvedimento: 'ESECUZIONE' };
export function sottocartellaAtto(atto) {
  const descrizione = (atto.descrizione || '').replace(/[\\/]+/g, '-').trim();
  return [CARTELLE_ATTO[atto.tipo] || atto.tipo, descrizione].filter(Boolean).join(' - ');
}
// esecuzione del provvedimento: solo dopo l'emissione del primo provvedimento (numero o data), come
// collettivi.SQL_PROVVEDIMENTO_EMESSO nel backend, che decide
export const provvedimentoEmesso = (ricorso) => !!(String(ricorso?.numero_sentenza || '').trim() || ricorso?.data_sentenza);
const MSG_ESECUZIONE = "L'esecuzione del provvedimento si crea dopo l'emissione del primo provvedimento: "
  + 'serve un provvedimento con numero o data nella sezione sentenza.';

function CampiPrezzi({ f, set }) {
  return (
    <>
      <label><span style={etichetta}>Compenso (€)</span>
        <input type="number" step="0.01" min="0" onWheel={senzaRotella} className="input-filtro" style={{ width: 100 }} value={f.compenso}
          onChange={e => set('compenso', e.target.value)} /></label>
      <label><span style={etichetta}>Compenso convenzionati (€)</span>
        <input type="number" step="0.01" min="0" onWheel={senzaRotella} className="input-filtro" style={{ width: 100 }} value={f.compenso_convenzionati}
          onChange={e => set('compenso_convenzionati', e.target.value)} /></label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
        <input type="checkbox" checked={f.netto} onChange={e => set('netto', e.target.checked)} /> Cifra al netto
        {f.netto && (f.compenso || f.compenso_convenzionati) && (
          <span style={{ color: '#718096' }}>→ lordo {fmtEur(f.compenso ? lordoDaNetto(f.compenso) : null)}
            {' / '}{fmtEur(f.compenso_convenzionati ? lordoDaNetto(f.compenso_convenzionati) : null)}</span>)}
      </label>
    </>
  );
}

const prezzo = (v, netto) => (v === '' || v === null || v === undefined ? null : (netto ? lordoDaNetto(v) : Number(v)));

// Ricorrenti di un atto: uno alla volta o tutti gli idonei (ricorrenti nell'atto del ricorso; in una
// rinunzia chi è già in rinunzia non è tra gli idonei). Si aggiungono anche dopo la data di
// proposizione: in una rinunzia proposta chi entra passa subito a Rinunzia.
function AggiuntaRicorrentiAtto({ atto, idonei, onCambio }) {
  const [scelto, setScelto] = useState('');
  const [err, setErr] = useState(null);
  const [aggiungendo, inviaUno] = useInvio();
  const [aggiungendoTutti, inviaTutti] = useInvio();
  const fuori = idonei.filter(a => !(atto.aderenti || []).some(x => x.codice_cliente === a.codice_cliente));
  const esegui = (invia, fn, fallback) => invia(async () => {
    setErr(null);
    try { await fn(); onCambio(); } catch (e) { setErr(msgErrore(e, fallback)); }
  });
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="input-filtro" value={scelto} onChange={e => setScelto(e.target.value)} disabled={!fuori.length}>
          <option value="">{fuori.length ? '— aggiungi un ricorrente —' : 'Nessun altro ricorrente idoneo'}</option>
          {fuori.map(a => <option key={a.codice_cliente} value={a.codice_cliente}>{nominativo(a)}</option>)}
        </select>
        <button style={btn(ACCENTO_ATTI, false, !scelto || aggiungendo)} disabled={!scelto || aggiungendo}
          onClick={() => esegui(inviaUno, async () => {
            await aggiungiAderenteAtto(atto.id, { codice_cliente: Number(scelto) }); setScelto('');
          }, 'Ricorrente non aggiunto')}>Aggiungi</button>
        <button style={btn(ACCENTO_ATTI, true, !fuori.length || aggiungendoTutti)} disabled={!fuori.length || aggiungendoTutti}
          onClick={() => esegui(inviaTutti, () => aggiungiTuttiAtto(atto.id), 'Ricorrenti non aggiunti')}>
          Aggiungi tutti ({fuori.length})</button>
      </div>
      {err && <div style={errore}>{err}</div>}
    </div>
  );
}

// Nuovo atto: tipo, data di proposizione, CU (non per la rinunzia), prezzi e note; i ricorrenti si
// aggiungono dopo, dalla Modifica o dal dettaglio dell'atto.
function FormAtto({ atto, idonei = [], onSalva, onAnnulla, onCambio, esecuzioneDisponibile = true }) {
  const nuovo = !atto;
  const [f, setF] = useState({
    tipo: atto?.tipo || 'motivi_aggiunti', descrizione: atto?.descrizione || '', data_deposito: atto?.data_deposito || '',
    cu_dovuto: !!atto?.cu_dovuto, cu_importo: atto?.cu_importo ?? '', cu_f24_pagato: !!atto?.cu_f24_pagato,
    compenso: atto?.compenso ?? '', compenso_convenzionati: atto?.compenso_convenzionati ?? '', netto: false,
    note: atto?.note || '',
  });
  const [err, setErr] = useState(null);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const rinunzia = f.tipo === 'rinunzia';
  const esecuzioneBloccata = nuovo && f.tipo === 'esecuzione_provvedimento' && !esecuzioneDisponibile;
  const [inviando, invia] = useInvio();
  const salva = () => invia(async () => {
    setErr(null);
    const dati = {
      descrizione: f.descrizione.trim() || null,
      data_deposito: f.data_deposito || null, cu_dovuto: rinunzia ? false : f.cu_dovuto,
      cu_importo: rinunzia || !f.cu_dovuto || f.cu_importo === '' ? null : Number(f.cu_importo),
      cu_f24_pagato: rinunzia ? false : f.cu_f24_pagato,
      compenso: prezzo(f.compenso, f.netto), compenso_convenzionati: prezzo(f.compenso_convenzionati, f.netto),
      note: f.note || null,
    };
    if (nuovo) dati.tipo = f.tipo;
    try { await onSalva(dati); } catch (e) { setErr(msgErrore(e, 'Atto non salvato')); }
  });
  return (
    <div style={riquadro}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        <label><span style={etichetta}>Tipo</span>
          <select className="input-filtro" value={f.tipo} disabled={!nuovo} onChange={e => set('tipo', e.target.value)}>
            {Object.entries(TIPI_ATTO).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select></label>
        <label><span style={etichetta}>Descrizione</span>
          <input className="input-filtro" style={{ width: 220 }} value={f.descrizione} placeholder="es. Graduatorie ATA 2027"
            onChange={e => set('descrizione', e.target.value)} /></label>
        <label><span style={etichetta}>Data di proposizione</span>
          <input type="date" className="input-filtro" value={f.data_deposito} onChange={e => set('data_deposito', e.target.value)} /></label>
        {!rinunzia && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
              <input type="checkbox" checked={f.cu_dovuto} onChange={e => set('cu_dovuto', e.target.checked)} /> CU dovuto</label>
            {f.cu_dovuto && (
              <>
                <label><span style={etichetta}>Importo CU (€)</span>
                  <input type="number" step="0.01" min="0" onWheel={senzaRotella} className="input-filtro" style={{ width: 90 }} value={f.cu_importo}
                    onChange={e => set('cu_importo', e.target.value)} /></label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  <input type="checkbox" checked={f.cu_f24_pagato} onChange={e => set('cu_f24_pagato', e.target.checked)} /> F24 pagato</label>
              </>
            )}
          </>
        )}
        <CampiPrezzi f={f} set={set} />
      </div>
      <label style={{ display: 'block', marginTop: 8 }}><span style={etichetta}>Note</span>
        <textarea className="input-filtro" rows={2} style={{ width: '100%', ...nota }} value={f.note}
          onChange={e => set('note', e.target.value)} /></label>
      {nuovo && <div style={{ fontSize: 11, color: '#718096', marginTop: 6 }}>
        I ricorrenti si aggiungono dopo la registrazione, uno alla volta o tutti insieme.</div>}
      {!nuovo && <AggiuntaRicorrentiAtto atto={atto} idonei={idonei} onCambio={onCambio} />}
      {esecuzioneBloccata && <div style={errore}>{MSG_ESECUZIONE}</div>}
      {err && <div style={errore}>{err}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button style={btn(ACCENTO_ATTI, true, inviando || esecuzioneBloccata)} disabled={inviando || esecuzioneBloccata}
          onClick={salva}>{nuovo ? "Registra l'atto" : 'Salva'}</button>
        <button style={btn('#718096')} onClick={onAnnulla}>Annulla</button>
      </div>
    </div>
  );
}

function DettaglioAtto({ atto, ricorso, idonei, puoModificare, onCambio }) {
  const { haPermesso } = useAuth();
  const puoDocumenti = haPermesso('documenti.leggi');
  const [err, setErr] = useState(null);
  const [documenti, setDocumenti] = useState(null);     // codice cliente con il fascicolo aperto
  const proposto = !!atto.data_deposito;
  const esegui = async (fn, fallback) => {
    setErr(null);
    try { await fn(); onCambio(); } catch (e) { setErr(msgErrore(e, fallback)); }
  };
  return (
    <div style={{ ...riquadro, background: 'white' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={th}>Ricorrente</th><th style={th}>Codice fiscale</th><th style={{ ...th, textAlign: 'right' }}>Compenso</th>
          <th style={th}>Pagato</th><th style={th} /></tr></thead>
        <tbody>
          {atto.aderenti.map(a => ([
            <tr key={a.codice_cliente}>
              <td style={td}>{nominativo(a)}</td>
              <td style={{ ...td, fontFamily: 'monospace' }}>{a.codice_fiscale || '—'}</td>
              <td style={{ ...td, textAlign: 'right' }}>
                {puoModificare ? (
                  <input type="number" step="0.01" min="0" onWheel={senzaRotella} className="input-filtro" style={{ width: 90, textAlign: 'right' }}
                    defaultValue={a.compenso ?? ''} aria-label={`Compenso ${nominativo(a)}`}
                    onBlur={e => {
                      const v = e.target.value === '' ? null : Number(e.target.value);
                      if (v !== a.compenso) esegui(() => modificaAderenteAtto(atto.id, a.codice_cliente, { compenso: v }), 'Compenso non salvato');
                    }} />) : fmtEur(a.compenso)}
              </td>
              <td style={td}>
                <input type="checkbox" checked={!!a.compenso_pagato} disabled={!puoModificare} aria-label={`Pagato ${nominativo(a)}`}
                  onChange={e => esegui(() => modificaAderenteAtto(atto.id, a.codice_cliente, { compenso_pagato: e.target.checked }),
                    'Pagamento non salvato')} />
              </td>
              <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {puoDocumenti && <button style={btn('#2B6CB0', documenti === a.codice_cliente)}
                  onClick={() => setDocumenti(d => (d === a.codice_cliente ? null : a.codice_cliente))}>Documenti</button>}{' '}
                {puoModificare && !proposto && (
                  <button style={btn('#C53030')} onClick={() => {
                    if (!confermaUscita("Togliere dall'atto", a, `${TIPI_ATTO[atto.tipo]} · ${ricorso.codice_ricorso}`)) return;
                    esegui(() => togliAderenteAtto(atto.id, a.codice_cliente), 'Ricorrente non tolto');
                  }}>Togli</button>)}
              </td>
            </tr>,
            documenti === a.codice_cliente && (
              <tr key={`${a.codice_cliente}-documenti`}><td colSpan={5} style={{ padding: '0 8px' }}>
                <CaricaDocumenti tipo="ricorso" id={ricorso.id} aderente={a.codice_cliente}
                  titolo={`Documenti personali di ${nominativo(a)}`} sottocartellaPredefinita={sottocartellaAtto(atto)} />
              </td></tr>)]))}
          {!atto.aderenti.length && <tr><td colSpan={5} style={{ ...td, color: '#718096' }}>Nessun ricorrente nell'atto.</td></tr>}
        </tbody>
      </table>
      {puoModificare && <AggiuntaRicorrentiAtto atto={atto} idonei={idonei} onCambio={onCambio} />}
      {proposto && <div style={{ fontSize: 11, color: '#718096', marginTop: 6 }}>
        Atto proposto: i ricorrenti si aggiungono ma non si tolgono (per togliere serve un atto di rinunzia).</div>}
      {err && <div style={errore}>{err}</div>}
    </div>
  );
}

export function CardAttiSuccessivi({ ricorso, versione = 0, onCambio }) {
  const { haPermesso } = useAuth();
  const puoModificare = haPermesso('ricorsi.modifica');
  const [atti, setAtti] = useState([]);
  const [idonei, setIdonei] = useState([]);           // ricorrenti nell'atto del ricorso
  const [err, setErr] = useState(null);
  const [nuovo, setNuovo] = useState(false);
  const disponibili = depositato(ricorso);            // gli atti si creano solo dopo il deposito
  const [aperto, setAperto] = useState(null);      // {id, cosa: 'dettaglio'|'modifica'}

  const carica = useCallback(() => {
    getAtti(ricorso.id).then(r => setAtti(r.data)).catch(e => setErr(msgErrore(e, 'Atti non disponibili')));
    getRicorrentiNellAtto(ricorso.id).then(r => setIdonei(r.data)).catch(() => setIdonei([]));
  }, [ricorso.id]);
  useEffect(() => { carica(); }, [carica, versione]);
  // una rinunzia cambia lo stato delle adesioni: si ricaricano anche i Ricorrenti
  const cambiato = () => { carica(); onCambio && onCambio(); };
  const apri = (id, cosa) => setAperto(x => (x && x.id === id && x.cosa === cosa ? null : { id, cosa }));

  return (
    <div style={card(ACCENTO_ATTI)}>
      <div style={testata(ACCENTO_ATTI)}>
        <span>Atti successivi <span style={{ color: '#718096', fontWeight: 500 }}>· {atti.length}</span></span>
        {puoModificare && disponibili && <button style={btn(ACCENTO_ATTI, true)} onClick={() => setNuovo(v => !v)}>+ Nuovo atto</button>}
      </div>
      {!disponibili && <div style={{ fontSize: 12, color: '#718096', marginBottom: 6 }}>
        Motivi aggiunti e rinunzie sono disponibili dopo il deposito del ricorso.</div>}
      {nuovo && disponibili && <FormAtto onAnnulla={() => setNuovo(false)} esecuzioneDisponibile={provvedimentoEmesso(ricorso)}
        onSalva={async (dati) => { await creaAtto(ricorso.id, dati); setNuovo(false); cambiato(); }} />}
      {err && <div style={errore}>{err}</div>}
      {!atti.length && !nuovo && disponibili && <div style={{ fontSize: 12, color: '#718096' }}>Nessun motivo aggiunto né rinunzia.</div>}
      {atti.map(a => {
        const qui = aperto && aperto.id === a.id ? aperto.cosa : null;
        const conAderenti = a.totali.n_aderenti > 0;
        return (
          <div key={a.id} style={{ borderBottom: '1px solid #edf2f7', padding: '6px 0' }}>
            <div data-testid={`atto-${a.id}`} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
              <b style={{ minWidth: 120 }}>{TIPI_ATTO[a.tipo]}</b>
              {a.descrizione && <span>{a.descrizione}</span>}
              <span>{a.data_deposito ? `proposto il ${fmtData(a.data_deposito)}` : <i style={{ color: '#B7791F' }}>non ancora proposto</i>}</span>
              {a.cu_dovuto && <span>CU {fmtEur(a.cu_importo)}{a.cu_f24_pagato ? ' · F24 pagato' : ' · F24 da pagare'}</span>}
              <span>{a.totali.n_aderenti} ricorrenti</span>
              <span style={{ color: '#276749' }}>incassato {fmtEur(a.totali.incassato)}</span>
              <span style={{ color: '#C05621' }}>da incassare {fmtEur(a.totali.da_incassare)}</span>
              {a.totali.senza_compenso > 0 && <span style={{ color: '#718096' }}>{a.totali.senza_compenso} senza compenso</span>}
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button style={btn(ACCENTO_ATTI)} onClick={() => apri(a.id, 'dettaglio')}>Ricorrenti</button>
                {puoModificare && <button style={btn(ACCENTO_ATTI)} onClick={() => apri(a.id, 'modifica')}>Modifica</button>}
                {puoModificare && (
                  <button style={btn('#C53030', false, conAderenti)} disabled={conAderenti}
                    title={conAderenti ? `L'atto ha ${a.totali.n_aderenti} aderenti: non si può eliminare` : undefined}
                    onClick={async () => {
                      if (!window.confirm(`Eliminare l'atto (${TIPI_ATTO[a.tipo]}) senza ricorrenti?`)) return;
                      try { await eliminaAtto(a.id); cambiato(); } catch (e) { setErr(msgErrore(e, 'Atto non eliminato')); }
                    }}>Elimina</button>)}
              </span>
            </div>
            {a.note && <div style={{ ...nota, fontSize: 12, padding: '4px 8px', borderRadius: 4, marginTop: 4 }}>{a.note}</div>}
            {qui === 'dettaglio' && <DettaglioAtto atto={a} ricorso={ricorso} idonei={idonei} puoModificare={puoModificare} onCambio={cambiato} />}
            {qui === 'modifica' && <FormAtto atto={a} idonei={idonei} onCambio={cambiato} onAnnulla={() => setAperto(null)}
              onSalva={async (dati) => { await modificaAtto(a.id, dati); setAperto(null); cambiato(); }} />}
          </div>
        );
      })}
    </div>
  );
}

// ── Card in elenco e in albero ────────────────────────────────────────────────

// Nome del ricorso fissato al deposito, se c'è; altrimenti "COGNOME Nome ed altri" (correzioni del
// 23/10: senza il numero, che resta nel badge "Collettivo · N aderenti"), solo il nome con un solo
// ricorrente attivo (n_aderenti: in attesa di documentazione o documentazione completa, dal backend).
// Unico punto per card in elenco, albero e vista cliente (RicorsoCard).
export function intestazioneCollettivo(ricorso) {
  if (ricorso.collettivo && ricorso.denominazione) return ricorso.denominazione;      // fissato al deposito
  const nome = `${ricorso.cognome || ''} ${ricorso.nome_cliente || ''}`.trim();
  return ricorso.collettivo && (ricorso.n_aderenti || 0) > 1 ? `${nome} ed altri` : nome;
}

export function BadgeCollettivo({ ricorso }) {
  const n = ricorso.n_aderenti || 0;
  return (
    <span title="Ricorso collettivo: ricorrenti in attesa di documentazione o con documentazione completa"
      style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
        color: ACCENTO_RICORRENTI, background: '#FFF7ED', border: `1px solid ${ACCENTO_RICORRENTI}60` }}>
      Collettivo · {n} aderent{n === 1 ? 'e' : 'i'}
    </span>
  );
}

// ── Appello di un collettivo: ricorrenti da portare in appello ──────────────────
// Si parte dai ricorrenti nell'atto del primo grado (in attesa di documentazione o documentazione
// completa), tutti selezionati. onChange riceve null quando sono tutti (il backend li prende tutti) o
// l'elenco dei codice_cliente scelti. Capofila dell'appello: il primo in ordine alfabetico tra i
// scelti (l'elenco arriva ordinato dal backend, come per il calcolo del capofila), passato a onCapofila.
export function SceltaAderentiAppello({ padreId, onChange, onCapofila }) {
  const [ricorrenti, setRicorrenti] = useState([]);
  const [esclusi, setEsclusi] = useState(new Set());
  const [err, setErr] = useState(null);
  const capofilaCb = useRef(onCapofila);
  capofilaCb.current = onCapofila;
  useEffect(() => {
    getRicorrentiNellAtto(padreId).then(r => {
      setRicorrenti(r.data);
      if (capofilaCb.current) capofilaCb.current(r.data[0] || null);
    }).catch(e => setErr(msgErrore(e, 'Ricorrenti del primo grado non disponibili')));
  }, [padreId]);
  const scelti = ricorrenti.filter(a => !esclusi.has(a.codice_cliente));
  const cambia = (codice) => {
    const s = new Set(esclusi);
    if (s.has(codice)) s.delete(codice); else s.add(codice);
    setEsclusi(s);
    const nuovi = ricorrenti.filter(a => !s.has(a.codice_cliente));
    onChange(s.size ? nuovi.map(a => a.codice_cliente) : null);
    if (onCapofila) onCapofila(nuovi[0] || null);
  };
  return (
    <div style={{ ...riquadro, borderColor: `${ACCENTO_RICORRENTI}60`, marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: ACCENTO_RICORRENTI, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Ricorrenti dell'appello · {scelti.length} di {ricorrenti.length} nell'atto del primo grado
      </div>
      <div style={{ fontSize: 11, color: '#718096', margin: '2px 0 6px' }}>
        Entrano tutti in attesa di documentazione (serve la nuova procura), con il prezzo dell'appello secondo la loro convenzione.
      </div>
      <div data-testid="capofila-appello" style={{ fontSize: 12, marginBottom: 6 }}>
        {scelti.length
          ? <>Capofila dell'appello: <b>{nominativo(scelti[0])}</b> <span style={{ color: '#718096' }}>· primo in ordine alfabetico tra i ricorrenti scelti</span></>
          : <span style={{ color: '#C53030' }}>Scegli almeno un ricorrente</span>}
      </div>
      {err && <div style={errore}>{err}</div>}
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {ricorrenti.map(a => (
          <label key={a.codice_cliente} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, padding: '2px 0' }}>
            <input type="checkbox" checked={!esclusi.has(a.codice_cliente)} onChange={() => cambia(a.codice_cliente)} />
            {nominativo(a)} <span style={{ color: '#718096' }}>{a.codice_fiscale}</span>
            <BadgeStatoAdesione stato={a.stato} />
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Contenitore per la scheda: le due card si aggiornano a vicenda ─────────────
// onCambio (facoltativo) ricarica la scheda dopo ogni modifica: badge e conteggi subito aggiornati.

export default function SezioniCollettivo({ ricorso, onCambio }) {
  const [versione, setVersione] = useState(0);
  const avanti = useCallback(() => { setVersione(v => v + 1); if (onCambio) onCambio(); }, [onCambio]);
  return (
    <>
      <CardRicorrenti ricorso={ricorso} versione={versione} onCambio={avanti} />
      <CardAttiSuccessivi ricorso={ricorso} versione={versione} onCambio={avanti} />
    </>
  );
}
